import Styles from './PerformanceInsights.module.scss'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { AvatarWithPlatformIcon } from 'screens/Analytics/components/ExternalIntegrationSelect/AvatarWithPlatformIcon'
import { Check02 } from 'blaze-ui/icons/16'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useInsights } from 'hooks/queries/externalIntegrations/useInsights'
import { useIntegrationInsightsProgress } from './useIntegrationInsightsProgress'
import { useIntegrationLoadingProgress } from './useIntegrationLoadingProgress'
import { useCallback, useEffect, useRef } from 'react'
import { LoadingMessage } from './LoadingMessage'

export interface IntegrationInsightsProps {
  integration: ExternalIntegration
  brandKitId: number
  onComplete: () => void
}

export const IntegrationInsights = ({ integration, brandKitId, onComplete }: IntegrationInsightsProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { data: insights = [] } = useInsights({ externalIntegrationId: integration.id })
  const hasCompletedRef = useRef(false)
  const invalidationIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const invalidateQueries = useCallback(async () => {
    if (!integration) return

    queryClient.invalidateQueries([
      'workspaces',
      workspaceId,
      'externalIntegrations',
      integration.id,
      'channelInsights',
    ])
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'strategicRecommendations'])
  }, [integration, workspaceId, brandKitId, queryClient])

  const { data: insightsProgress, isLoading } = useIntegrationInsightsProgress(integration.id, {
    pollUntilFinal: true,
    onComplete: () => {
      // Start polling invalidateQueries every 1s until insights array is not empty
      invalidationIntervalRef.current = setInterval(() => invalidateQueries(), 1000)
    },
    enabled: !!integration.id && (!!integration?.brain_channel_insights_bid || !!integration.posts_fetching_bid),
  })

  const { loadingProgress, loadingLabel } = useIntegrationLoadingProgress(insightsProgress)

  useEffect(() => {
    if (insights.length > 0) {
      // Clear interval when insights array is not empty anymore
      if (invalidationIntervalRef.current) {
        clearInterval(invalidationIntervalRef.current)
        invalidationIntervalRef.current = null
      }

      // Call onComplete when insights are available
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true
        onComplete()
      }
    }
  }, [insights.length, onComplete])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (invalidationIntervalRef.current) {
        clearInterval(invalidationIntervalRef.current)
      }
    }
  }, [])

  return (
    <div key={integration.id} className={Styles.insightItem}>
      <div className={Styles.header}>
        <div className={Styles.provider}>
          <AvatarWithPlatformIcon size="xs" avatar={integration.picture} platform={integration?.provider} />
          <Heading level={4}>{integration.name}</Heading>
        </div>
        <div className={Styles.check}>
          <div className={Styles.checkIcon}>
            <Check02 size={8} color="var(--light-100)" />
          </div>
          <Text variant="smallList" color="#20A14F">
            Connected
          </Text>
        </div>
      </div>
      {(isLoading || !insights.length) && <LoadingMessage label={loadingLabel} progress={loadingProgress} />}
      {!isLoading && !!insights.length && (
        <div className={Styles.insightData}>
          <div className={Styles.insightDataPositive}>
            <div className={Styles.insightDataHeader}>
              ✅<Heading level={5}>Know what drives results</Heading>
            </div>
            <ul className={Styles.insightDataContent}>
              {insights
                .filter((insight) => insight.sentiment === 'positive')
                .map((insight, index) => (
                  <li key={`${insight.external_integration.id}-${index}`} className={Styles.insightDataContentItem}>
                    <Text variant="secondary" color="var(--dark-90)">
                      {insight.content}
                    </Text>
                  </li>
                ))}
            </ul>
          </div>
          <div className={Styles.insightDataNegative}>
            <div className={Styles.insightDataHeader}>
              ⚠️ <Heading level={5}>Areas for improvement</Heading>
            </div>
            <ul className={Styles.insightDataContent}>
              {insights
                .filter((insight) => insight.sentiment === 'negative')
                .map((insight, index) => (
                  <li key={`${insight.external_integration.id}-${index}`} className={Styles.insightDataContentItem}>
                    <Text variant="secondary" color="var(--dark-90)">
                      {insight.content}
                    </Text>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
